/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { LandingPageHeaderH2 } from "~/components/landing/LandingText";
import Ratings, { StarRating } from "~/components/landing/Ratings";
import { Picture } from "~/components/ui/picture";

/**
 * Ported from bouncieweb's `LandingPageComponents/ReviewsAndRatings.js`.
 *
 * A blueLight3 band holding a heading, a row of translucent review cards, and
 * the shared app-store `Ratings` block. The card row scrolls horizontally on
 * small screens and centers into a 3-up row from md.
 *
 * The original lazy-loaded `StarRating` via `next/dynamic({ ssr: false })`
 * purely to defer the canvas bundle. `StarRating` already does all of its canvas
 * work inside effects (SSR renders an empty <canvas>), so it is imported
 * directly here.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

export type LandingReview = {
  image: string;
  name: string;
  rating: number;
  title: React.ReactNode;
  body: React.ReactNode;
};

function Review({ image, name, rating, title, body }: LandingReview) {
  return (
    // ReviewWrapper
    <div
      className={cn(
        "flex w-[89%] shrink-0 grow-0 flex-col gap-2 rounded-[10px] p-6 [text-wrap:wrap]",
        "min-[431px]:w-[60%] sm:w-[45%] md:w-auto md:flex-[0_1_33%]",
      )}
      style={{ background: "#ffffffaa", color: colors.fadedBlack3 }}
    >
      {/* HostInfo */}
      <div className="flex items-center justify-between">
        {/* HostImageAndName */}
        <div className="flex items-center gap-2">
          <Picture src={image} alt={`Headshot of ${name}`} className="h-auto w-10" />
          <div className="text-[18px] font-bold">{name}</div>
        </div>
        <StarRating
          rating={rating}
          activeFill={colors.orange}
          inactiveFill="#aaa"
          className="h-[18px] max-w-[100px]"
        />
      </div>
      <div className="font-bold">{title}</div>
      <p className="m-0 text-[14px]">&ldquo;{body}&rdquo;</p>
    </div>
  );
}

export default function ReviewsAndRatings({
  heading,
  reviewsData,
}: {
  heading: React.ReactNode;
  reviewsData: readonly LandingReview[];
}) {
  return (
    // Section
    <div className="py-8 md:py-12" style={{ background: colors.blueLight3 }}>
      {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
      <div className="wrap-legacy overflow-visible">
        <LandingPageHeaderH2 className="m-0">{heading}</LandingPageHeaderH2>
      </div>

      {/* ReviewsWrapper */}
      <div
        className={cn(
          "mx-auto mt-4 mb-8 flex w-full gap-4 overflow-x-scroll px-4 pt-8 pb-0 [scrollbar-gutter:stable_both-edges]",
          "md:mt-12 md:mb-12 md:justify-center md:overflow-x-visible md:px-4",
          "lg:w-[924px] lg:p-0 xl:w-[1000px]",
        )}
      >
        {reviewsData.map((review, index) => (
          <Review key={index} {...review} />
        ))}
      </div>

      {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
      <div className="wrap-legacy">
        <Ratings />
      </div>
    </div>
  );
}
